<template>
  <div>
    <client-only>
      <cms-section
        disable-lazy-hydrate
        :first-section="firstElement"
        :page-context
        :section="{ name: `segmentation-${content.id}`, items: segmentContents }"
      />
      <template #fallback>
        <div class="skeleton">
          <cms-section
            class="invisible"
            disable-lazy-hydrate
            :first-section="firstElement"
            :page-context
            :section="{ name: `segmentation-${content.id}`, items: segmentContents }"
          />
        </div>
      </template>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import type { Segmentation } from '#types/components/cms/segmentation'
import type { Content } from '#types/content'
import mappings from '#content/mappings'

const { content, firstElement, pageContext } = defineProps<{
  content: Segmentation
  firstElement?: boolean
  pageContext?: boolean
}>()

const auth = useAuthStore()
const { isLoyaltyMember, isWranxProfile, isIpaProfile, isWranxOrIpaProfile, isAthleteProfile }
  = storeToRefs(useUserStore())
const cart = useCartStore()
const query = useRouteQuery('p13n_testcontext', '{}')

const segmentsMap = computed(() => ({
  'loggedout': !auth.loggedIn,
  'loggedin': auth.loggedIn,
  'loyalty-loggedin': isLoyaltyMember.value,
  'non-loyalty-loggedin': auth.loggedIn && !isLoyaltyMember.value,
  'employee-loggedin': auth.loggedIn && auth.isEmployee,
  'non-employee-loggedin': auth.loggedIn && !auth.isEmployee,
  'cart-empty': !cart.totalItems,
  'cart-not-empty': cart.totalItems,
  'ipa-wranx-loggedin': auth.loggedIn && isWranxOrIpaProfile.value,
  'non-ipa-wranx-loggedin': auth.loggedIn && !isWranxOrIpaProfile.value,
  'wranx-loggedin': auth.loggedIn && isWranxProfile.value,
  'non-wranx-loggedin': auth.loggedIn && !isWranxProfile.value,
  'ipa-loggedin': auth.loggedIn && isIpaProfile.value,
  'non-ipa-loggedin': auth.loggedIn && !isIpaProfile.value,
  'athletes-loggedin': auth.loggedIn && isAthleteProfile.value,
  'non-athletes-loggedin': auth.loggedIn && !isAthleteProfile.value,
}))

// override can be set in the CMS to preview specific segments
const segmentOverride = computed(() => JSON.parse(query.value)?.segment)

const segmentContents: Ref<Content[]> = computed(() => {
  const segment = segmentOverride.value
    ? content.segments?.find(({ id }) => id === segmentOverride.value)
    : content.segments?.find(({ id }) => segmentsMap.value[id])
  return ((isClient() && segment?.targets)
    || content.baselines
    || [] as Content[]
  ).map((segmentationContent) => ({
    ...resolveContentComponent(segmentationContent, mappings),
    sectionId: content.id
  } as Content))
})

const emptySections = useState<Record<string, boolean>>('emptySections', () => ({}))

watch(segmentContents, (contents) => {
  emptySections.value[content.id] = !contents.length
}, { immediate: true })
</script>
